import useStartAgentWorkChat from '@/hooks/useStartAgentWorkChat';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { StyleSheet, ScrollView, ActivityIndicator, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useUIStore, useAgentStore, useRouteStore, useAuthStore, useChatInputStore } from '@/store';
import { useLocalSearchParams, useRouter } from 'expo-router';
import useResponsiveness from '@/hooks/useResponsiveness';
import useComposerKeyboardHeight from '@/hooks/useComposerKeyboardHeight';
import ChatInput from '@/components/ChatInput/ChatInput';
import { CHAT_INPUT_STYLE, MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS } from '@/constants/config';
import MetaHead from '@/components/MetaHead';
import PageHeaderUpgradeButton from '@/components/Buttons/PageHeaderUpgradeButton';
import ChatPageHeader from '@/components/ChatPageHeader';
import AgentWorkSuggestions from '@/components/Agents/AgentWorkSuggestions';
import StatusContainer from '@/components/StatusContainer';
import ChatInputContainer from '@/components/ChatInput/ChatInputContainer';

const loadingStyles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

const createStyles = (isXS, colors, chatInputStyle, messageWidth, bottomInset = 0, keyboardHeight = 0) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.solid_color_primary,
  },
  centeredContainer: {
    flex: 1,
    width: '100%',
    minWidth: 200,
    maxWidth: '60%',
    marginHorizontal: 'auto',
    justifyContent: 'center',
    alignItems: 'center',
  },
  messagesContainer: {
    flex: 1,
    position: 'relative',
    paddingBottom: (chatInputStyle === CHAT_INPUT_STYLE.FLOATING ? (isXS ? 240 : 140) : 170) + (Platform.OS !== 'web' ? Math.max(bottomInset, 0) : 0),
  },
  messagesContentContainer: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyStateContainer: {
    alignItems: 'center',
    maxWidth:
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW : 
      messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM : MESSAGE_WIDTH_AMOUNTS.MEDIUM,
    width: '100%',
    padding: 20,
    marginBottom: -80,
  },
  suggestionsContainer: {
    position: 'relative',
    left: 0,
    right: 0,
    paddingHorizontal: 0,
  },
  suggestionsScroll: {
    maxWidth: 'auto',
    marginHorizontal: 'auto',
    paddingLeft: 10,
    paddingVertical: 10,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pageHeaderUpgradeButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    marginBottom: 20,
  },
  inputContainer: {
    width: '100%',
    zIndex: 2,
    position: isXS ? 'absolute' : 'relative',
    bottom: isXS ? 0 : 'auto',
    left: isXS ? 0 : 'auto',
    right: isXS ? 0 : 'auto',
    backgroundColor: isXS ? colors.solid_color_primary : 'transparent',
    paddingBottom: Math.max(keyboardHeight, isXS ? bottomInset : 0),
  },
});

const AgentChatPage = React.memo(({ agent }) => {
  const { colors } = useTheme();
  const router = useRouter();
  const chatInputStyle = useUIStore(state => state.chatInputStyle);
  const messageWidth = useUIStore(state => state.messageWidth);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);

  // Split store selectors to prevent unnecessary re-renders
  const isLoadingAgent = useAgentStore(state => state.isLoadingAgent);
  const agentError = useAgentStore(state => state.agentError);

  const { setCurrentAgentId } = useRouteStore(state => state.actions);
  const { isXS } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const [inputValue, setInputValue] = useState('');
  const keyboardHeight = useComposerKeyboardHeight();
  const [inputContainerHeight, setInputContainerHeight] = useState(0);

  const handleInputContainerLayout = useCallback((event) => {
    const nextHeight = Math.ceil(event?.nativeEvent?.layout?.height || 0);
    setInputContainerHeight((prev) => (Math.abs(prev - nextHeight) > 1 ? nextHeight : prev));
  }, []);

  const styles = useMemo(() => createStyles(isXS, colors, chatInputStyle, messageWidth, insets.bottom, keyboardHeight), [isXS, colors, chatInputStyle, messageWidth, insets.bottom, keyboardHeight]);
  const messageContainerStyle = useMemo(() => {
    const staticPaddingBottom = (chatInputStyle === CHAT_INPUT_STYLE.FLOATING ? (isXS ? 240 : 140) : 170) + (Platform.OS !== 'web' ? Math.max(insets.bottom, 0) : 0);
    return [
      styles.messagesContainer,
      { paddingBottom: Math.max(staticPaddingBottom, inputContainerHeight) },
    ];
  }, [styles.messagesContainer, chatInputStyle, isXS, insets.bottom, inputContainerHeight]);
  const memoizedAgentIds = useMemo(() => [agent?.id], [agent?.id]);
  const mockConversationId = `new-${agent?.id}`;

  // Set current agent ID
  useEffect(() => {
    setCurrentAgentId(agent?.id);
    return () => setCurrentAgentId(null);
  }, [agent?.id, setCurrentAgentId]);

  // Set the chat input context for this bot page
  const setContext = useChatInputStore(state => state.setContext);
  useEffect(() => {
    if (agent?.id) {
      setContext(`new-${agent.id}`);
    }
  }, [agent?.id, setContext]);

  const handleSendSuggestion = useCallback(async (suggestion) => {
    setInputValue(suggestion);
  }, [setInputValue]);

  const startAgentWorkChat = useStartAgentWorkChat();
  const handleSend = useCallback((formattedMessage) => {
    if (!formattedMessage) return false;
    return startAgentWorkChat(agent, {
      source: 'bot_page',
      prompt: formattedMessage.text,
      fileIds: formattedMessage.fileIds,
      requireWork: true,
    });
  }, [agent, startAgentWorkChat]);

  if (!agent) {
    if (isLoadingAgent) {
      return (
        <View style={styles.centeredContainer}>
          <ActivityIndicator size="large" color={colors.text_color_secondary} />
        </View>
      );
    }
    return (
      <View style={styles.centeredContainer}>
        <StatusContainer
          type='error'
          title={agentError || 'Unable to load bot'}
          actionText='Go Back to Bots'
          onAction={() => router.push('/agents')}
        /> 
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <MetaHead 
        title={agent?.name ? `Work with ${agent.name}` : "Work with Bot"}
        description={agent?.description || "Give this AI bot a job in Work mode."}
        noindex
      />
      <ScrollView 
        style={messageContainerStyle}
        contentContainerStyle={styles.messagesContentContainer}
        showsVerticalScrollIndicator={true}
        keyboardDismissMode="interactive"
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.emptyStateContainer}>
          {
            !isPlus && (
              <View style={styles.pageHeaderUpgradeButtonContainer}>
                <PageHeaderUpgradeButton />
              </View>
            )
          }

          <ChatPageHeader
            name={agent?.name}
            description={agent?.description}
            image={agent?.avatarPath}
            showPlusBadge={!isPlus}
            isAgent={true}
          />

          {!isXS && (
            <AgentWorkSuggestions
              agent={agent}
              onSelect={handleSendSuggestion}
              isMobile={false}
            />
          )}
        </View>
      </ScrollView>

      <View
        style={styles.inputContainer}
        onLayout={handleInputContainerLayout}
      >
        <ChatInputContainer>
          {isXS && (
            <AgentWorkSuggestions
              agent={agent}
              onSelect={handleSendSuggestion}
              inputValue={inputValue}
              isMobile={true}
              animateOnMobile={true}
            />
          )}

          <ChatInput
            key={mockConversationId}
            onSend={handleSend}
            placeholder={`What should ${agent?.name || 'this bot'} work on?`}
            value={inputValue}
            agentIds={memoizedAgentIds}
            conversationId={mockConversationId}
            isWorkMode={true}
            hideVoiceMode={true}
          />
        </ChatInputContainer>
      </View>
    </View>
  );
}, (prevProps, nextProps) => {
  return prevProps?.agent?.id === nextProps?.agent?.id;
});

function AgentChatRoute() {
  const { id: agentId } = useLocalSearchParams();
  const { colors } = useTheme();
  const router = useRouter();
  const userId = useAuthStore(state => state.user?.id);
  const isAuthenticated = Boolean(userId);
  const getAgentById = useAgentStore(state => state.getAgentById);
  const fetchAgentById = useAgentStore(state => state.fetchAgentById);
  const agentError = useAgentStore(state => state.agentError);

  // Re-subscribing to `agents` keeps this reactive so the page re-renders
  // (and picks up the freshly-fetched agent) once it lands.
  const agents = useAgentStore(state => state.agents);
  const agent = agents[agentId] || getAgentById(agentId);
  const [isWaitingForAgent, setIsWaitingForAgent] = useState(!agent);

  // Agent metadata is public — anonymous visitors can land on `/g/:id`
  // and browse the agent header / starter prompts before deciding to
  // sign up. The send action itself is gated downstream by `ChatInput`.
  //
  // Keep this effect keyed on `agentId` only. Depending on `agent` /
  // `isLoadingAgent` made the effect re-run (and its cleanup fire) the moment
  // `fetchAgentById` flipped `isLoadingAgent`, which invalidated the in-flight
  // request's `mounted` flag before it resolved — so `setIsWaitingForAgent(false)`
  // never ran and the page was stuck on the loader (most visible on a hard
  // navigation with an empty store, e.g. pasting a copied agent link in a new tab).
  useEffect(() => {
    let cancelled = false;

    const loadAgent = async () => {
      if (!agentId) return;
      if (getAgentById(agentId)) {
        setIsWaitingForAgent(false);
        return;
      }
      setIsWaitingForAgent(true);
      try {
        await fetchAgentById(agentId);
      } catch (error) {
        console.error('Error loading agent:', error);
      } finally {
        if (!cancelled) setIsWaitingForAgent(false);
      }
    };

    loadAgent();

    return () => {
      cancelled = true;
    };
  }, [agentId, fetchAgentById, getAgentById]);
  
  if (isWaitingForAgent) {
    return (
      <View style={loadingStyles.container}>
        <ActivityIndicator size="large" color={colors.text_color_secondary} />
      </View>
    );
  }

  if (!isAuthenticated) {
    return (
      <View style={[loadingStyles.container, { padding: 24, backgroundColor: colors.solid_color_primary }]}>
        <StatusContainer
          type="empty"
          title="Sign in to chat with this bot"
          message="Custom bots are private to your account. Please sign in to access your custom bots and start chatting."
          actionText="Sign In"
          actionIcon="user"
          onAction={() => router.push('/login')}
        />
      </View>
    );
  }

  if (!agent) {
    return (
      <View style={[loadingStyles.container, { padding: 24, backgroundColor: colors.solid_color_primary }]}>
        <StatusContainer
          type="error"
          title={agentError || 'Bot not found'}
          message="This bot is private, archived, or does not exist. If this is your custom bot, please sign in with your account to access it."
          actionText="Explore All Bots"
          onAction={() => router.push('/agents')}
        />
      </View>
    );
  }

  return (
    <AgentChatPage agent={agent} />
  );
}

export default React.memo(AgentChatRoute);
